// src/redux/couponSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { baseUrl } from "../utils/utils";

// ✅ Fetch coupons list
export const fetch_coupons = createAsyncThunk(
  "coupon/list",
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const body = new FormData();
      body.append("action", "list");
      body.append("page", page);
      body.append("limit", limit);

      const response = await fetch(`${baseUrl}coupon_code.php`, {
        method: "POST",
        body,
      });

      const res = await response.json();
      return res;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// ✅ Create coupon
export const create_coupon = createAsyncThunk(
  "coupon/create",
  async (data, { rejectWithValue }) => {
    try {
      const body = new FormData();
      body.append("action", "create");
      body.append("coupon_code", data.coupon_code);
      body.append("description", data.description);
      body.append("discount_amount", data.discount_amount);
      body.append("validity", data.validity);
      body.append("status", data.status);

      // ✅ Append product codes
      if (data.prod_code && Array.isArray(data.prod_code)) {
        data.prod_code.forEach((code) => {
          body.append("prod_code[]", code);
        });
      }

      // ✅ Append customer IDs
      if (data.customer_id && Array.isArray(data.customer_id)) {
        data.customer_id.forEach((cid) => {
          body.append("customer_id[]", cid);
        });
      }

      const response = await fetch(`${baseUrl}coupon_code.php`, {
        method: "POST",
        body,
      });

      const res = await response.json();
      return res;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// ✅ Update coupon
export const update_coupon = createAsyncThunk(
  "coupon/update",
  async (data, { rejectWithValue }) => {
    try {
      const body = new FormData();
      body.append("action", "update");
      body.append("id", data.id);
      body.append("coupon_code", data.coupon_code);
      body.append("description", data.description);
      body.append("discount_amount", data.discount_amount);
      body.append("validity", data.validity);
      body.append("status", data.status);

      // ✅ Append product codes
      if (data.prod_code && Array.isArray(data.prod_code)) {
        data.prod_code.forEach((code) => {
          body.append("prod_code[]", code);
        });
      }

      // ✅ Append customer IDs
      if (data.customer_id && Array.isArray(data.customer_id)) {
        data.customer_id.forEach((cid) => {
          body.append("customer_id[]", cid);
        });
      }

      const response = await fetch(`${baseUrl}coupon_code.php`, {
        method: "POST",
        body,
      });

      const res = await response.json();
      return res;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// ✅ Delete coupon
export const delete_coupon = createAsyncThunk(
  "coupon/delete",
  async (id, { rejectWithValue }) => {
    try {
      const body = new FormData();
      body.append("action", "delete");
      body.append("id", id);

      const response = await fetch(`${baseUrl}coupon_code.php`, {
        method: "POST",
        body,
      });

      const res = await response.json();
      return res;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const initialState = {
  loading: false,
  success: false,
  error: null,
  message: "",
  coupons: [],
  summary: null,
  totalCount: 0,
  currentPage: 1,
};

const couponSlice = createSlice({
  name: "coupon",
  initialState,
  reducers: {
    resetCouponState: (state) => {
      state.loading = false;
      state.success = false;
      state.error = null;
      state.message = "";
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch coupons
      .addCase(fetch_coupons.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetch_coupons.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload;
        if (data.success) {
          state.coupons = data.data || [];
          state.summary = data.summary || null;
          state.totalCount = data.totalcount || 0;
        } else {
          state.error = data.message || "Failed to fetch coupons";
        }
      })
      .addCase(fetch_coupons.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      })

      // Create coupon
      .addCase(create_coupon.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(create_coupon.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload;
        if (data.success) {
          state.success = true;
          state.message = data.message || "Coupon created successfully";
        } else {
          state.error = data.message || "Failed to create coupon";
        }
      })
      .addCase(create_coupon.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      })

      // Update coupon
      .addCase(update_coupon.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(update_coupon.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload;
        if (data.success) {
          state.success = true;
          state.message = data.message || "Coupon updated successfully";
        } else {
          state.error = data.message || "Failed to update coupon";
        }
      })
      .addCase(update_coupon.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      })

      // Delete coupon
      .addCase(delete_coupon.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(delete_coupon.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload;
        if (data.success) {
          state.success = true;
          state.message = data.message || "Coupon deleted successfully";
        } else {
          state.error = data.message || "Failed to delete coupon";
        }
      })
      .addCase(delete_coupon.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      });
  },
});

export const { resetCouponState } = couponSlice.actions;
export default couponSlice.reducer;
